import { Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { Title } from '@angular/platform-browser';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { BannerComponent } from '../../shared/components/banner/banner.component';
import { BackendErrorType } from '../../shared/services/errors/backend.errors';
import { CorporateDesignService } from '../../shared/services/corporate-design.service';
import { BackendService } from 'app/shared/services/backend.service';
import { LandingpageConfigurationStateService } from 'app/shared/services/state/landingpage-configuration-state.service';
import { MainLayoutComponent } from 'app/shared/components/layouts/main-layout/main-layout.component';

@Component({
  selector: 'ez-personal-access-token',
  imports: [ReactiveFormsModule, MainLayoutComponent, ButtonComponent, BannerComponent],
  templateUrl: './personal-access-token.component.html',
  styleUrl: './personal-access-token.component.scss',
})
export class PersonalAccessTokenComponent {
  private backendService = inject(BackendService);
  private router = inject(Router);
  private logger = inject(NGXLogger);
  private landingpageConfigurationStateService = inject(LandingpageConfigurationStateService);
  private titleService = inject(Title);
  private corporateDesignService = inject(CorporateDesignService);

  defaultTitle: string;

  personalAccessTokenForm = new FormGroup({
    personalAccessToken: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  readonly errorMessage = signal<string | null>(null);

  constructor() {
    this.defaultTitle = this.titleService.getTitle();
    this.titleService.setTitle('Ihr persönlicher Zugangscode');
    this.tokenInput.events
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.errorMessage.set(this.currentErrorMessage()));
  }

  get tokenInput(): AbstractControl {
    return this.personalAccessTokenForm.controls.personalAccessToken;
  }

  private currentErrorMessage(): string | null {
    const tokenInput = this.tokenInput;
    if (!tokenInput.invalid || !(tokenInput.dirty || tokenInput.touched)) {
      return null;
    }
    if (tokenInput.errors?.['required']) {
      return 'Fehler: Dies ist ein Pflichtfeld. Bitte geben Sie Ihren Zugangscode ein.';
    } else if (tokenInput.errors?.[BackendErrorType.CLIENT_ERROR]) {
      return 'Fehler: Bitte überprüfen Sie Ihre Eingabe.';
    }
    return 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
  }

  onSubmit() {
    const personalAccessToken = this.personalAccessTokenForm.controls['personalAccessToken'].value;

    this.backendService.submitPersonalAccessToken(personalAccessToken).subscribe({
      next: (response) => {
        this.logger.debug('Request Landingpage Configuration:', {
          requestParam: personalAccessToken,
          response: response,
        });
        this.landingpageConfigurationStateService.updateConfig(response, personalAccessToken);

        //add orga specific colors, (fav)icons and fonts
        this.corporateDesignService.updateTheme(response.organisation.corporateDesign, response.assetBucketUrl);

        // Reset title
        this.titleService.setTitle(this.defaultTitle);

        this.router.navigate(['case']);
      },
      error: (error: Error) => {
        if (error.message === BackendErrorType.ALREADY_SENT) {
          this.router.navigate(['already-sent']);
        } else if (error.message === BackendErrorType.VERIFICATION_ERROR) {
          this.router.navigate(['verification-error']);
        } else {
          this.tokenInput.setErrors({ [error.message]: true });
        }
      },
    });
  }
}
